import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  CssBaseline,
  Checkbox,
  Backdrop,
  CircularProgress,
  Fade,
  DialogTitle,
  DialogContent,

  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Tooltip,
  FormControlLabel,
  FormGroup,
} from '@mui/material';

import 'handsontable/dist/handsontable.full.css';
import 'handsontable/styles/handsontable.min.css';
import 'handsontable/styles/ht-theme-main.min.css';
import dayjs from 'dayjs';

import { uploadFile, fetchApi, uploadTable } from '../services/api';
import SideMenu from '../components/SideMenu';
import SmartEditor from '../smarteditor/component/sEditor';
import { SmartEditorRef } from '../smarteditor/component/sEditor';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AlertSnackbar from '../components/AlertSnackbar';
import ConfirmDialog from '../components/ConfirmDialog';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Dialog from '@mui/material/Dialog';
import { registerAllModules } from 'handsontable/registry';
import ImageGalleryDialog from '../components/ImageGalleryDialog';
import ImageIcon from '@mui/icons-material/Image';

registerAllModules();
interface ApiCityResponse {
  table: CityData[];
}

interface ApiPlaceResponse {
  table: PlaceData[];
}

interface ItineraryData {
  일정명: string;
  상품가: string;
  유효시작일: string;
  유효종료일: string;
  엑셀파일경로: string;
  일정JSON: string;
  내용: string;
  공유: boolean;
  일정번호?: number;
  출발월: string;
  출발시간: string;
  도착시간: string;
}

interface CityData {
  코드명: string;
  국가한글명: string;
  도시번호: number;
  도시한글명: string;
}

interface PlaceData {
  장소번호: number;
  장소한글명: string;
}

interface SelectedPlaceData extends PlaceData {
  도시번호: number;
  도시한글명: string;
}

interface MainPagePlaceData {
  장소번호: string;
  장소한글명: string;
  도시번호: string;
  도시한글명: string;
}

export const UploadHwp_new = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const itinerary = location.state?.itinerary;
  const editorRef = useRef<SmartEditorRef>(null);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isFormEnabled, setIsFormEnabled] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [isActive, setIsActive] = useState(false);
  // 간편일정 데이터
  const [itineraryData, setItineraryData] = useState<ItineraryData>({
    일정명: '',
    상품가: '',
    유효시작일: dayjs().format('YYYY-MM-DD'),
    유효종료일: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    엑셀파일경로: '',
    일정JSON: '',
    내용: '',
    공유: true,
    출발월: '',
    출발시간: '',
    도착시간: ''
  });

  const handleEditorChange = (content: string) => {
    // 내용이 변경될 때마다 itineraryData를 업데이트하지 않고,
    // 에디터 내용만 저장
    setEditorContent(content);

    // 에디터에 내용이 있을 때만 버튼 활성화 (100자 이상)
    const hasContent = content.trim().length > 1000;
    if (isFormEnabled !== hasContent) {
      setIsFormEnabled(hasContent);
    }
  };

  // 이전 내용을 저장하기 위한 ref
  const prevContentRef = useRef<string>('');

  useEffect(() => {
    const checkEditorContent = () => {
      if (editorRef.current) {
        const currentContent = editorRef.current.getContent();
        // 이전 내용과 다를 때만 handleEditorChange 호출
        if (currentContent !== prevContentRef.current) {
          prevContentRef.current = currentContent;
          handleEditorChange(currentContent);
        }
      }
    };

    // 체크 간격을 2초에서 5초로 늘림
    // const interval = setInterval(checkEditorContent, 5000);

    // return () => clearInterval(interval);
  }, []);

  // 에디터 내용이 변경되었을 때만 itineraryData 업데이트
  useEffect(() => {
    if (editorContent) {
      setItineraryData(prev => ({
        ...prev,
        내용: editorContent
      }));
    }
  }, [editorContent]);

  const handleHwpApply = async () => {
    try {
      setLoading(true);
      setIsFormEnabled(true);
      const content = editorRef.current?.getContent() || '';
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      const tables = tempDiv.querySelectorAll('table');
      
      if (!tables || tables.length === 0) {
        throw new Error('테이블을 찾을 수 없습니다.');
      }

      // 새로운 병합된 테이블 생성
      const mergedTable = document.createElement('table');
      const mergedTbody = document.createElement('tbody');
      mergedTable.appendChild(mergedTbody);

      // 모든 테이블의 행을 순회하면서 병합
      tables.forEach((table) => {
        const rows = table.rows;
        for (let i = 0; i < rows.length; i++) {
          const newRow = document.createElement('tr');
          const cells = rows[i].cells;
          
          for (let j = 0; j < cells.length; j++) {
            const newCell = document.createElement('td');
            const originalCell = cells[j];
            
            // colspan과 rowspan 값 복사
            if (originalCell.hasAttribute('colspan')) {
              newCell.setAttribute('colspan', originalCell.getAttribute('colspan')!);
            }
            if (originalCell.hasAttribute('rowspan')) {
              newCell.setAttribute('rowspan', originalCell.getAttribute('rowspan')!);
            }
            
            // 셀 내용 복사 및 정리
            let cellContent = originalCell.innerHTML;

            // span 태그 제거 (innerHTML만 유지)
            const tempSpanDiv = document.createElement('div');
            tempSpanDiv.innerHTML = cellContent;
            const spanElements = tempSpanDiv.getElementsByTagName('span');
            while (spanElements.length > 0) {
              const span = spanElements[0];
              span.outerHTML = span.innerHTML;
            }
            cellContent = tempSpanDiv.innerHTML;

            // 모든 스타일 속성 제거
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = cellContent;
            const allElements = tempDiv.getElementsByTagName('*');
            for (let k = 0; k < allElements.length; k++) {
              allElements[k].removeAttribute('style');
              allElements[k].removeAttribute('class');
            }
            cellContent = tempDiv.innerHTML;

            // p 태그를 br로 변환
            cellContent = cellContent.replace(/<p[^>]*>(.*?)<\/p>/g, (match, content) => {
              return content + '<br>';
            });
            
            // 연속된 br 태그를 하나로 통합
            cellContent = cellContent.replace(/(<br\s*\/?>\s*)+/g, '<br>');
            
            // 마지막 br 태그 제거
            cellContent = cellContent.replace(/<br>$/, '');

            newCell.innerHTML = cellContent;
            newRow.appendChild(newCell);
          }
          
          mergedTbody.appendChild(newRow);
        }
      });

      // 모든 속성 제거
      mergedTable.removeAttribute('style');
      mergedTable.removeAttribute('class');
      
      // 테이블 문자열로 변환
      const tableHtml = mergedTable.outerHTML;
      
      // API 호출
      const response = await uploadTable(tableHtml);

      if (response.status !== 200) {
        throw new Error(`서버 응답 오류: ${response.status}`);
      }

      const responseData = response.data;

      if (!responseData.subData) {
        throw new Error('파일 처리 결과가 올바르지 않습니다.');
      }

      const { subData } = responseData;
      // 1. 먼저 에디터 내용 설정
      if (editorRef.current) {
        const contentWithData = `
                <div class="itinerary-data" style="display: none;">${JSON.stringify(subData)}</div>
                ${responseData.html || ''}
              `;
        editorRef.current.setContent(contentWithData);
      }
      setIsDataLoaded(true);

      // 도시와 장소 매칭 처리
      if (subData.locations && subData.places) {
        try {
          const postData = [
            { 컬럼명: '@도시키워드', 값: subData.locations },
            { 컬럼명: '@장소키워드', 값: subData.places },
          ];
          const matchingResponse = await fetchApi<any>('ITN_S_간편일정_도시장소매칭', postData);
          if (matchingResponse.status === 200) {
            // 매칭 결과를 subData에 추가
            const cities = matchingResponse.data.table;
            const places = matchingResponse.data.table1;
            setCities(cities);
            setPlaces(places);
            // 초기화: 모든 항목이 선택된 상태로 시작
            setDeselectedCities(new Set());
            setDeselectedPlaces(new Set());
          } else {
            console.error('도시/장소 매칭 실패:', matchingResponse);
          }
        } catch (error) {
          console.error('도시/장소 매칭 중 오류:', error);
        }
      }

      // 폼 데이터 업데이트
      const updatedData = {
        ...itineraryData,
        내용: responseData.html,
        일정명: subData.상품명,
        상품가: subData.price,
        유효시작일: dayjs().format('YYYY-MM-DD'),
        유효종료일: dayjs().add(1, 'year').format('YYYY-MM-DD'),
        엑셀파일경로: responseData.file_url,
        일정JSON: JSON.stringify(subData),
        출발시간: subData.출발시간,
        도착시간: subData.도착시간,
        출발월: subData.출발월,

      };
      setItineraryData(updatedData);

      // 메인 에디터에 내용 반영 (상태 업데이트 후)
      setTimeout(() => {
        if (editorRef.current) {
          const contentWithData = `
            <div class="itinerary-data" style="display: none;">${JSON.stringify(subData)}</div>
            ${responseData.html}
          `;
          editorRef.current.setContent(contentWithData);
        }
      }, 100);
      setIsActive(true);
      setLoading(false);

    } catch (error) {
      console.error('한글 에디터 데이터 처리 중 오류:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : '처리 중 오류가 발생했습니다.',
        severity: 'error',
      });
      setIsDataLoaded(false);
      setLoading(false);
    }
  };

  const [cities, setCities] = useState<Array<{ 도시번호: string; 도시한글명: string }>>([]);
  const [places, setPlaces] = useState<MainPagePlaceData[]>([]); // 빈 배열로 초기화
  const [deselectedCities, setDeselectedCities] = useState<Set<string>>(new Set());
  const [deselectedPlaces, setDeselectedPlaces] = useState<Set<string>>(new Set());

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    message: '',
    onConfirm: () => { },
  });

  const [openCityDialog, setOpenCityDialog] = useState(false);
  const [cityData, setCityData] = useState<CityData[]>([]);
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<CityData | null>(null);
  const [placeData, setPlaceData] = useState<PlaceData[]>([]);
  const [selectedPlaces, setSelectedPlaces] = useState<SelectedPlaceData[]>([]);
  const [placeFilter, setPlaceFilter] = useState('');
  const filteredPlaces = placeData.filter((place) =>
    place.장소한글명.toLowerCase().includes(placeFilter.toLowerCase())
  );

  const [newFile, setNewFile] = useState<File | null>(null);
  const [newFilePath, setNewFilePath] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);

  const handleImageSelect = (imageUrl: string) => {
    // 이미지 URL을 에디터에 삽입하는 로직 추가
    setIsImageGalleryOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleCancel = () => {
    setConfirmDialog({
      open: true,
      message: '작성을 취소하시겠습니까?',
      onConfirm: () => {
        navigate(-1);
        setConfirmDialog({ ...confirmDialog, open: false });
      },
    });
  };

  useEffect(() => {
    if (itinerary) {
      setItineraryData(itinerary);
      setIsDataLoaded(true);
      // handleEditOpen();
    } else {
      setIsDataLoaded(false);
    }
  }, [itinerary]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawerWidth = 240;

  const handleNewFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setNewFile(file);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await uploadFile(file, 'fileupload');

        const responseData = response.data;

        if (!responseData.file_url) {
          throw new Error('파일 처리 결과가 올바르지 않습니다.');
        }

        itineraryData.내용 = itineraryData.내용.replace(itineraryData.엑셀파일경로, responseData.file_url);

        setItineraryData((prev) => ({
          ...prev,
          엑셀파일경로: responseData.file_url,
        }));
        setSnackbar({
          open: true,
          message: '파일이 업로드되었습니다.',
          severity: 'success',
        });
      } catch (error) {
        console.error('File upload error:', error);
        setSnackbar({
          open: true,
          message: '파일 업로드에 실패했습니다.',
          severity: 'error',
        });
      }
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('로그인이 필요합니다.');
      }
      const decodedToken = JSON.parse(atob(token!.split('.')[1]));

      // 현재 에디터 내용 로깅
      const content = editorRef.current?.getContent() || '';
      setItineraryData({ ...itineraryData, 내용: content });

      // 선택된 도시와 장소 리스트 생성
      const selectedCityList =
        (cities || [])
          .filter((city) => !deselectedCities.has(city.도시한글명))
          .map((city) => city.도시번호)
          .join(',') || '';

      const selectedPlaceList =
        (places || [])
          .filter((place) => !deselectedPlaces.has(place.장소한글명))
          .map((place) => place.장소번호)
          .join(',') || '';

      if (!itineraryData.일정명) {
        throw new Error('일정명을 입력해주세요.');
      }
      // DB에 일정 정보 저장
      const procedureName = 'ITN_T_간편일정등록';
      const excelFilePath = newFilePath || itineraryData.엑셀파일경로;
      const postData = [
        { 컬럼명: '@일정명', 값: itineraryData.일정명 },
        { 컬럼명: '@상품가', 값: itineraryData.상품가 },
        { 컬럼명: '@유효시작일', 값: itineraryData.유효시작일 },
        { 컬럼명: '@유효종료일', 값: itineraryData.유효종료일 },
        { 컬럼명: '@엑셀파일경로', 값: excelFilePath },
        { 컬럼명: '@일정JSON', 값: itineraryData.일정JSON },
        { 컬럼명: '@내용', 값: content },
        { 컬럼명: '@유저번호', 값: decodedToken.UserNo.toString() },
        { 컬럼명: '@업체번호', 값: decodedToken.CompanyNo.toString() },
        { 컬럼명: '@공유', 값: itineraryData.공유 ? 1 : 0 },
        { 컬럼명: '@도시번호리스트', 값: selectedCityList },
        { 컬럼명: '@장소번호리스트', 값: selectedPlaceList },
        { 컬럼명: '@내유저번호', 값: decodedToken.UserNo.toString() },
        { 컬럼명: '@내업체번호', 값: decodedToken.CompanyNo.toString() },
        { 컬럼명: '@출발월', 값: itineraryData.출발월 },
        { 컬럼명: '@출발시간', 값: itineraryData.출발시간 },
        { 컬럼명: '@도착시간', 값: itineraryData.도착시간 },
      ];

      const response = await fetchApi(procedureName, postData);
      if (response && response.status === 200) {
        setSnackbar({
          open: true,
          message: '일정이 등록되었습니다.',
          severity: 'success',
        });
        navigate('/itinerary-list');
      } else {
        setSnackbar({
          open: true,
          message: '일정 등록에 실패했습니다.',
          severity: 'error',
        });
      }
    } catch (err: any) {
      console.error('Submit error:', err);
      setSnackbar({
        open: true,
        message: err.message || '저장 중 오류가 발생했습니다.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setItineraryData((prevData) => ({ ...prevData, 공유: e.target.checked }));
  };

  // 도시/장소 설정 창 열기
  const handleOpenCityDialog = async () => {
    setOpenCityDialog(true);

    try {
      const response = await fetchApi<ApiCityResponse>('ITN_S_도시국가리스트', [{ 컬럼명: '@업체번호', 값: '1' }]);

      if (response.data?.table) {
        setCityData(response.data.table);

        // 메인창에서 선택 해제된 장소 제외
        const filteredPlaces = places.filter((place) => !deselectedPlaces.has(place.장소한글명));
        const uniquePlaces = Array.from(
          new Map(
            filteredPlaces.map((place) => [
              place.장소번호,
              {
                장소번호: Number(place.장소번호),
                장소한글명: place.장소한글명,
                도시번호: Number(place.도시번호),
                도시한글명: place.도시한글명,
              },
            ])
          ).values()
        );
        setSelectedPlaces(uniquePlaces);

        const lastPlace = places[places.length - 1];
        const selectedCity = response.data.table.find((city) => Number(city.도시번호) === Number(lastPlace.도시번호));

        if (selectedCity) {
          setSelectedCode(selectedCity.코드명);
          setSelectedCountry(selectedCity.국가한글명);
          setSelectedCity(selectedCity);

          const placeResponse = await fetchApi<ApiPlaceResponse>('ITN_S_장소리스트', [
            { 컬럼명: '@도시번호', 값: selectedCity.도시번호.toString() },
          ]);

          if (placeResponse.data?.table) {
            setPlaceData(placeResponse.data.table);
          }
        }
      }
    } catch (error) {
      console.error('도시 데이터 로드 실패:', error);
    }
  };

  const handleSavePlaces = () => {
    if (selectedPlaces.length > 0) {
      // 선택된 장소들을 메인창에 추가
      const newPlaces = selectedPlaces.map((place) => ({
        장소번호: place.장소번호.toString(),
        장소한글명: place.장소한글명,
        도시번호: place.도시번호.toString(),
        도시한글명: place.도시한글명,
      }));
      setPlaces(newPlaces);

      // 선택된 도시들을 중복 없이 추가
      const newCities: Array<{ 도시번호: string; 도시한글명: string }> = [];
      const addedCityNumbers = new Set<string>();

      // 선택된 장소들의 도시 정보 추가
      selectedPlaces.forEach((place) => {
        const cityNumber = place.도시번호.toString();
        // 도시번호가 있고 도시한글명이 있는 경우에만 추가
        if (cityNumber && place.도시한글명 && !addedCityNumbers.has(cityNumber)) {
          addedCityNumbers.add(cityNumber);
          newCities.push({
            도시번호: cityNumber,
            도시한글명: place.도시한글명,
          });
        }
      });

      // 기존 도시들 중 선택되지 않은 도시들도 유지 (빈 도시는 제외)
      cities.forEach((city) => {
        if (city.도시번호 && city.도시한글명 && !addedCityNumbers.has(city.도시번호)) {
          newCities.push(city);
        }
      });

      setCities(newCities);
    }
    handleCloseCityDialog();
  };

  const handlePlaceSelect = (place: PlaceData) => {
    if (selectedCity) {
      setSelectedPlaces((prev) => {
        const exists = prev.some((p) => p.장소번호 === place.장소번호);
        if (exists) {
          // 이미 존재하면 삭제
          return prev.filter((p) => p.장소번호 !== place.장소번호);
        }
        // 새로운 장소 추가
        const newPlace: SelectedPlaceData = {
          장소번호: place.장소번호,
          장소한글명: place.장소한글명,
          도시번호: selectedCity.도시번호,
          도시한글명: selectedCity.도시한글명,
        };
        return [...prev, newPlace];
      });
    }
  };

  const handleCloseCityDialog = () => {
    setOpenCityDialog(false);
    setSelectedCode('');
    setSelectedCountry('');
    setSelectedCity(null);
    setPlaceData([]);
  };

  const handleCitySelect = async (city: CityData) => {
    setSelectedCity(city);
    try {
      const postData = [{ 컬럼명: '@도시번호', 값: city.도시번호.toString() }];

      const response = await fetchApi<ApiPlaceResponse>('ITN_S_장소리스트', postData);

      if (response.data?.table) {
        setPlaceData(response.data.table);
      } else {
        console.error('장소 데이터가 없습니다:', response);
      }
    } catch (error) {
      console.error('장소 데이터 로드 실패:', error);
    }
  };

  const handleInfo = () => {
    window.open('https://info.landbox.kr/goods?id=23288&UseCache=false&pageID=1809', '_blank');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* <CssBaseline /> */}

      <SideMenu mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pl: `${drawerWidth}px`, // 사이드메뉴 넓이 + 3으로 좌측 여백 설정
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          bgcolor: 'background.default',
        }}>
        <Container maxWidth="lg">
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mt: 2,
              bgcolor: 'background.paper',
              height: 'calc(100vh - 37px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
            }}>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
                  모든일정등록
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    ml: 3,
                  }}>
                  <Checkbox
                    checked={itineraryData.공유}
                    onChange={(e) => setItineraryData((prevData) => ({ ...prevData, 공유: e.target.checked }))}
                    disabled={!isFormEnabled}
                  />
                  <Typography component="span" sx={{ fontSize: '14px', color: 'text.secondary' }}>
                    공유 허용
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Button
                  startIcon={<LocationOnIcon />}
                  variant="contained"
                  onClick={handleOpenCityDialog}
                  disabled={!isFormEnabled}
                  sx={{ mr: 1, width: '150px', height: '40px' }}>
                  도시/장소 설정
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  startIcon={<SaveIcon />}
                  disabled={!isFormEnabled}
                  sx={{ mr: 1, width: '150px', height: '40px' }}>
                  등록
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  startIcon={<CloseIcon />}
                  sx={{ ml: 1, width: '150px', height: '40px' }}
                >
                  닫기
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item sx={{ width: 500 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', width: '45px' }}>일정명</Typography>
                  <TextField
                    fullWidth
                    // label="일정명"
                    value={itineraryData.일정명}
                    onChange={(e) => setItineraryData((prev) => ({ ...prev, 일정명: e.target.value }))}
                    disabled={!isFormEnabled}
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { height: '32px', width: '100%' } }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', width: '45px' }}>상품가</Typography>
                  <TextField
                    // label="상품가"
                    value={itineraryData.상품가}
                    onChange={(e) => setItineraryData((prev) => ({ ...prev, 상품가: e.target.value }))}
                    disabled={!isFormEnabled}
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { height: '32px', width: '220px', mr: 1 } }} />
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', width: '45px' }}>유효기간</Typography>
                  <TextField
                    // label="유효종료일"
                    type="date"
                    value={itineraryData.유효종료일}
                    onChange={(e) => setItineraryData((prev) => ({ ...prev, 유효종료일: e.target.value }))}
                    disabled={!isFormEnabled}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { height: '32px', width: '140px' } }} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', width: '45px' }}>출발월</Typography>
                  <TextField
                    value={itineraryData.출발월}
                    onChange={(e) => setItineraryData((prev) => ({ ...prev, 출발월: e.target.value }))}
                    disabled={!isFormEnabled}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { height: '32px', width: '162px', mr: 1 } }} />
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', width: '45px' }}>출발시간</Typography>
                  <TextField
                    value={itineraryData.출발시간}
                    onChange={(e) => setItineraryData((prev) => ({ ...prev, 출발시간: e.target.value }))}
                    disabled={!isFormEnabled}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { height: '32px', width: '65px', mr: 1 } }} />
                  <Typography sx={{ fontSize: '13px', color: 'text.secondary', width: '45px' }}>도착시간</Typography>
                  <TextField
                    value={itineraryData.도착시간}
                    onChange={(e) => setItineraryData((prev) => ({ ...prev, 도착시간: e.target.value }))}
                    disabled={!isFormEnabled}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { height: '32px', width: '65px' } }} />
                </Box>
                {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, mb: 2 }}> */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 2 }}>
                  <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                    {newFile ? newFile.name : '일정표 원본이 pdf, hwp일때 첨부해주세요.'}
                  </Typography>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleNewFileSelect}
                    style={{ display: 'none' }}
                    accept=".hwp,.pdf,.xlsx,.xls"
                  />
                  <Button variant="outlined" size="small" onClick={handleFileButtonClick} sx={{ height: '32px' }}>
                    원본 파일 추가
                  </Button>

                </Box>
                <Button variant="text"   onClick={handleInfo}  sx={{  height: '25px', fontSize: '14px' ,color:'primary.main', textDecoration: 'underline', cursor: 'pointer'}}>
                  {/* * 어울리는 웹일정표 변환을 위한 일정표 작성법 */}
                  * 일정변환이 안되거나, 이상할 때 여기를 보세요.
                  </Button>
                {/* </Box> */}
              </Grid>
              <Grid item sx={{ width: 620 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Paper elevation={0} sx={{ border: '1px solid #ccc', p: 1, pl: 2, pb: 1, overflow: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      도시
                    </Typography>
                    <FormGroup sx={{ gap: 0.5, flexDirection: 'row', flexWrap: 'wrap' }}>
                      {cities && cities.length > 0 ? (
                        cities.map((city) => (
                          <FormControlLabel
                            key={city.도시한글명}
                            control={
                              <Checkbox
                                checked={!deselectedCities.has(city.도시한글명)}
                                onChange={() => {
                                  const newDeselected = new Set(deselectedCities);
                                  if (deselectedCities.has(city.도시한글명)) {
                                    newDeselected.delete(city.도시한글명);
                                  } else {
                                    newDeselected.add(city.도시한글명);
                                  }
                                  setDeselectedCities(newDeselected);
                                }}
                                size="small"
                                disabled={!isFormEnabled}
                              />
                            }
                            label={city.도시한글명}
                            sx={{
                              margin: 0,
                              '& .MuiFormControlLabel-label': {
                                fontSize: '13px',
                              },
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          도시 데이터가 없습니다.
                        </Typography>
                      )}
                    </FormGroup>
                  </Paper>
                  <Paper elevation={0} sx={{ border: '1px solid #ccc', p: 1, pl: 2, pb: 1, overflow: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      장소
                    </Typography>
                    <FormGroup sx={{ gap: 0.5, flexDirection: 'row', flexWrap: 'wrap' }}>
                      {places && places.length > 0 ? (
                        places.map((place) => (
                          <FormControlLabel
                            key={place.장소한글명}
                            control={
                              <Checkbox
                                checked={!deselectedPlaces.has(place.장소한글명)}
                                onChange={() => {
                                  const newDeselected = new Set(deselectedPlaces);
                                  if (deselectedPlaces.has(place.장소한글명)) {
                                    newDeselected.delete(place.장소한글명);
                                  } else {
                                    newDeselected.add(place.장소한글명);
                                  }
                                  setDeselectedPlaces(newDeselected);
                                }}
                                size="small"
                                disabled={!isFormEnabled}
                              />
                            }
                            label={place.장소한글명}
                            sx={{
                              margin: 0,
                              '& .MuiFormControlLabel-label': {
                                fontSize: '13px',
                              },
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          장소 데이터가 없습니다.
                        </Typography>
                      )}
                    </FormGroup>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, width: '100%', backgroundColor: 'grey.200' }}>
              <Typography variant="body1" sx={{ fontSize: '12px', color: 'primary.main', backgroundColor: 'white', p: 1.38, px: 2, borderRadius: '30px' }}>
                1. 한글, 엑셀, 워드에서 일정표 영역을 복사해서  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; 2. 아래 붙여넣고(Ctrl + V) &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;  3. 오른쪽 [일정표 변환] 버튼을 클릭해 주세요;&nbsp;&nbsp;
                
              </Typography>
              <Box>
                <Button
                  startIcon={<ImageIcon />}
                  variant="outlined"
                  disabled={!isActive}
                  onClick={() => setIsImageGalleryOpen(true)}
                  sx={{ width: '150px', height: '40px', mr: 1, backgroundColor: 'white' }}>
                  이미지 갤러리
                </Button>
                <Button
                  startIcon={<LocationOnIcon />}
                  variant="contained"
                  onClick={handleHwpApply}
                  // disabled={!(editorContent.trim().length > 100)}
                  sx={{ width: '150px', height: '40px' }}>
                  일정표 변환
                </Button>
              </Box>
            </Box>
            <SmartEditor
              ref={editorRef}
              defaultValue={itineraryData?.내용 || ''}
              height={window.innerHeight - 500}

            />

          </Paper>
        </Container>
      </Box>
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
        open={loading}>
        <Fade in={loading} timeout={800}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <CircularProgress color="inherit" size={60} />
            <Typography
              variant="h6"

              /*component="div"*/

              sx={{
                textAlign: 'center',
                animation: 'pulse 2s infinite',
              }}>
              일정표를 업로드후 데이터를 분석하고 있습니다..
              <br />
              잠시만 기다려주세요
            </Typography>
          </Box>
        </Fade>
      </Backdrop>
      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      />
      <AlertSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
      <Dialog
        open={openCityDialog}
        onClose={handleCloseCityDialog}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            height: '860px',
            maxWidth: '1170px',
          },
        }}>
        <DialogTitle
          sx={{
            p: 3,
            height: '80px',
            bgcolor: '#1976d2',
            color: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <Typography variant="h6">도시/장소 선택</Typography>
          <Box>
            <Button
              variant="contained"
              onClick={handleSavePlaces}
              sx={{
                height: '40px',
                mr: 1,
                bgcolor: '#fff',
                color: '#1976d2',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                },
              }}>
              적용
            </Button>
            <Button
              variant="outlined"
              onClick={handleCloseCityDialog}
              sx={{
                height: '40px',
                borderColor: '#fff',
                color: '#fff',
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255, 255, 255, 0.08)',
                },
              }}>
              닫기
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mt: 1 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#333',
                }}>
                선택한 장소
              </Typography>
              <Box
                sx={{
                  p: 1,
                  height: '138px',
                  overflow: 'auto',
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                }}>
                {selectedPlaces.map((place) => (
                  <Tooltip key={place.장소번호} title={place.장소한글명}>
                    <Chip
                      label={place.장소한글명.length > 8 ? `${place.장소한글명.slice(0, 8)}...` : place.장소한글명}
                      onDelete={() => handlePlaceSelect(place)}
                      sx={{
                        m: 0.5,
                        width: 145,
                        fontSize: 12,
                        bgcolor: '#fff',
                        '& .MuiChip-label': {
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                        '& .MuiChip-deleteIcon': {
                          color: '#999',
                          '&:hover': {
                            color: '#666',
                          },
                        },
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#333',
                  }}>
                  지역
                </Typography>
                <List
                  sx={{
                    p: 2,

                    height: 180,
                    overflow: 'auto',
                    bgcolor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#bdbdbd',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}>
                  <Grid container>
                    {Array.from(new Set(cityData.map((item) => item.코드명))).map((codeName) => (
                      <Grid item xs={6} key={codeName}>
                        <ListItem disablePadding>
                          <ListItemButton
                            selected={selectedCode === codeName}
                            onClick={() => setSelectedCode(codeName)}
                            sx={{
                              fontSize: 12,
                              padding: '4px',
                              '&.Mui-selected': {
                                bgcolor: '#e3f2fd',
                                '&:hover': {
                                  bgcolor: '#e3f2fd',
                                },
                              },
                            }}>
                            <ListItemText primary={codeName} primaryTypographyProps={{ fontSize: 12 }} />
                          </ListItemButton>
                        </ListItem>
                      </Grid>
                    ))}
                  </Grid>
                </List>
              </Grid>
              <Grid item xs={4.5}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#333',
                  }}>
                  국가
                </Typography>
                <List
                  sx={{
                    p: 2,
                    height: 180,
                    overflow: 'auto',
                    bgcolor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#bdbdbd',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}>
                  <Grid container>
                    {selectedCode &&
                      Array.from(
                        new Set(cityData.filter((item) => item.코드명 === selectedCode).map((item) => item.국가한글명))
                      ).map((country) => (
                        <Grid item xs={4} key={country}>
                          <ListItem disablePadding>
                            <ListItemButton
                              selected={selectedCountry === country}
                              onClick={() => setSelectedCountry(country)}
                              sx={{
                                fontSize: 12,
                                padding: '4px',
                                '&.Mui-selected': {
                                  bgcolor: '#e3f2fd',
                                  '&:hover': {
                                    bgcolor: '#e3f2fd',
                                  },
                                },
                              }}>
                              <ListItemText primary={country} primaryTypographyProps={{ fontSize: 12 }} />
                            </ListItemButton>
                          </ListItem>
                        </Grid>
                      ))}
                  </Grid>
                </List>
              </Grid>
              <Grid item xs={4.5}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    fontSize: 13,
                    fontWeight: 400,
                    color: '#333',
                  }}>
                  도시
                </Typography>
                <List
                  sx={{
                    p: 2,
                    height: 180,
                    overflow: 'auto',
                    bgcolor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#bdbdbd',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}>
                  <Grid container>
                    {selectedCountry &&
                      cityData
                        .filter((item) => item.코드명 === selectedCode && item.국가한글명 === selectedCountry)
                        .map((city) => (
                          <Grid item xs={4} key={city.도시번호}>
                            <ListItem disablePadding>
                              <ListItemButton
                                selected={selectedCity?.도시번호 === city.도시번호}
                                onClick={() => handleCitySelect(city)}
                                sx={{
                                  fontSize: 12,
                                  padding: '4px',
                                  '&.Mui-selected': {
                                    bgcolor: '#e3f2fd',
                                    '&:hover': {
                                      bgcolor: '#e3f2fd',
                                    },
                                  },
                                }}>
                                <ListItemText primary={city.도시한글명} primaryTypographyProps={{ fontSize: 12 }} />
                              </ListItemButton>
                            </ListItem>
                          </Grid>
                        ))}
                  </Grid>
                </List>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#333',
                  }}>
                  장소 리스트
                </Typography>
                <TextField
                  size="small"
                  placeholder="장소 검색"
                  value={placeFilter}
                  onChange={(e) => setPlaceFilter(e.target.value)}
                  sx={{ width: '300px' }}
                />
              </Box>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  height: 260,
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#bdbdbd',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f5f5f5',
                  },
                }}>
                <Grid container spacing={1}>
                  {filteredPlaces.map((place) => (
                    <Grid item key={place.장소번호}>
                      <Button
                        variant={selectedPlaces.some((p) => p.장소번호 === place.장소번호) ? 'contained' : 'outlined'}
                        onClick={() => handlePlaceSelect(place)}
                        size="small"
                        sx={{
                          p: '5px',
                          width: 145,
                          fontSize: 12,
                          textTransform: 'none',
                          borderColor: '#e0e0e0',
                          '&.MuiButton-contained': {
                            bgcolor: '#1976d2',
                            color: '#fff',
                            '&:hover': {
                              bgcolor: '#1565c0',
                            },
                          },
                          '&.MuiButton-outlined': {
                            color: '#666',
                            '&:hover': {
                              borderColor: '#1976d2',
                              color: '#1976d2',
                              bgcolor: 'rgba(25, 118, 210, 0.04)',
                            },
                          },
                        }}>
                        {place.장소한글명.length > 8 ? `${place.장소한글명.slice(0, 12)}..` : place.장소한글명}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      <ImageGalleryDialog
        open={isImageGalleryOpen}
        onClose={() => setIsImageGalleryOpen(false)}
        onImageSelect={handleImageSelect}
        editorRef={editorRef}
      />
      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 0.6;
            }
            50% {
              opacity: 1;
            }
            100% {
              opacity: 0.6;
            }
          }
        `}
      </style>
    </Box>
  );
};
